import { useParams } from 'react-router';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { PageContent } from '@/layouts/Page';
import { DataGridContainer } from '@/components/DataGrid';
import { Toolbar } from '../shared';
import { ReceiptInfoDataGrid } from './data-grids';
import { ReceiptInfoDataGridMainToolbar } from './toolbars';
import { ReceiptStatus } from './ui';
import { useReceiptInfo } from './useReceiptInfo';
import { PharmacistsControlDetailReceiptInfoPathParams } from '@/router/paths/dorim-promo.path';
import { formatDateTime } from '@/helpers/format.helpers';
import { DEFAULT_FULL_DATE_TIME_FORMAT } from '@/constants/date-formats.constants';
import { RECEIPT_STATUS_CANCELLED } from '@/constants/receipt-status.constants';
import { useResetReceiptInfoStoreOnUnmount } from './receipt-info.store';
import { useToggle } from 'react-use';

const ReceiptInfoPage = () => {
  useResetReceiptInfoStoreOnUnmount();
  const { receiptId } = useParams<PharmacistsControlDetailReceiptInfoPathParams>();
  const [areDetailsVisible, toggleAreDetailsVisible] = useToggle(true);
  const { isFetching, data: receipt } = useReceiptInfo({ receiptId });

  if (!receipt) return null;

  const { cancel_reason, number, scanned_at, status, contractor } = receipt;
  const hasCancelReason = status === RECEIPT_STATUS_CANCELLED && !!cancel_reason;

  return (
    <PageContent>
      <Stack spacing={1} flexGrow={1}>
        <Toolbar isLoading={isFetching} isShowTabs={false}>
          <Stack width="100%">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography variant="Title/Bold/large">Чек №{number}</Typography>
                  <Typography variant="Headline/small" color="neutral.700">
                    {formatDateTime(scanned_at, DEFAULT_FULL_DATE_TIME_FORMAT)}
                  </Typography>
                </Stack>

                <ReceiptStatus status={status} />

                {hasCancelReason && (
                  <Typography variant="Body/medium" color="error">
                    {cancel_reason}
                  </Typography>
                )}
              </Stack>

              <IconButton sx={{ p: 1, color: 'secondary.main' }} onClick={toggleAreDetailsVisible}>
                {areDetailsVisible ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </IconButton>
            </Stack>

            <Collapse in={areDetailsVisible}>
              <Box mt={3}>
                <Divider />
                <Box mt={2} width="100%" display="flex" alignItems="center">
                  <Typography variant="Body/medium" color="neutral.700" component="span">
                    Юридическое лицо
                  </Typography>
                  <Typography variant="Body/medium" ml={1.5} component="span">
                    {contractor.name}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Stack>
        </Toolbar>

        <DataGridContainer>
          <ReceiptInfoDataGridMainToolbar />
          <ReceiptInfoDataGrid />
        </DataGridContainer>
      </Stack>
    </PageContent>
  );
};

export default ReceiptInfoPage;
